import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const AdminDuplicateStudents = () => {
  const navigate = useNavigate();
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDuplicates();
  }, []);

  const fetchDuplicates = async () => {
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

      const response = await fetch(`${API_URL}/api/admin/duplicate-students`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch duplicates');
      }

      const data = await response.json();
      setDuplicateGroups(data.duplicate_groups);
    } catch (err) {
      console.error('Error fetching duplicates:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading duplicates...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <button
            onClick={() => navigate('/admin')}
            className="mb-4 text-sm text-gray-600 hover:text-gray-900 inline-flex items-center"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Admin Dashboard
          </button>

          <h1 className="text-3xl font-bold text-gray-900">
            Duplicate Student Records
          </h1>
          <p className="text-gray-600 mt-2">
            Found {duplicateGroups.length} groups of duplicate records
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Duplicate Groups */}
        {duplicateGroups.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <div className="text-green-500 text-5xl mb-4">✓</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              No Duplicates Found
            </h2>
            <p className="text-gray-600">
              All student records have unique email addresses.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {duplicateGroups.map((group, groupIndex) => (
              <div key={groupIndex} className="bg-white rounded-lg shadow">
                {/* Group Header */}
                <div className="bg-yellow-50 border-b border-yellow-200 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {group.match_type === 'university_email' ? 'University Email' : 'Personal Email'}: {group.email}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {group.records.length} duplicate records found
                      </p>
                    </div>
                  </div>
                </div>

                {/* Records Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Email Fields
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Match Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Created At
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Applied This Cycle
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Verified Fields
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Relationships
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {group.records.map((record, recordIndex) => (
                        <tr key={record.id} className={recordIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          {/* ID */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            #{record.id}
                          </td>

                          {/* Name */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {record.first_name} {record.last_name}
                          </td>

                          {/* Email Fields - Show both university_email and university_email_verified */}
                          <td className="px-6 py-4 text-sm text-gray-900">
                            <div className="space-y-1">
                              <div>
                                <span className="font-medium text-gray-700">Self-Reported:</span>{' '}
                                <span className="text-gray-900">{record.university_email || 'N/A'}</span>
                              </div>
                              <div>
                                <span className="font-medium text-gray-700">Verified:</span>{' '}
                                <span className="text-gray-900">{record.university_email_verified || 'N/A'}</span>
                              </div>
                            </div>
                          </td>

                          {/* Match Type - Shows which field caused the duplicate */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              record.matched_on === 'both'
                                ? 'bg-purple-100 text-purple-800'
                                : record.matched_on === 'university_email'
                                  ? 'bg-blue-100 text-blue-800'
                                  : 'bg-green-100 text-green-800'
                            }`}>
                              {record.matched_on === 'both'
                                ? 'Both Fields'
                                : record.matched_on === 'university_email'
                                  ? 'Self-Reported'
                                  : 'Verified'}
                            </span>
                          </td>

                          {/* Created At */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {record.created_at ? new Date(record.created_at).toLocaleDateString() : 'N/A'}
                          </td>

                          {/* Applied This Cycle */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              record.applied_for_this_cycle === 'Yes'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {record.applied_for_this_cycle}
                            </span>
                          </td>

                          {/* Verified Fields */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {record.verified_fields_count}/4 verified
                          </td>

                          {/* Relationships */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            <div className="space-y-1">
                              {record.has_applied_history && (
                                <span className="block text-xs text-blue-600">• Applied History</span>
                              )}
                              {record.has_interactions && (
                                <span className="block text-xs text-purple-600">• Interactions</span>
                              )}
                              {record.has_update_tokens && (
                                <span className="block text-xs text-orange-600">• Tokens</span>
                              )}
                              {!record.has_applied_history && !record.has_interactions && !record.has_update_tokens && (
                                <span className="block text-xs text-gray-400">None</span>
                              )}
                            </div>
                          </td>

                          {/* Actions */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            {recordIndex === 0 ? (
                              <button
                                onClick={() => navigate(`/admin/merge-students/${record.id}/${group.records[1].id}`)}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                              >
                                Merge Records
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDuplicateStudents;
